import {
  streamChatLLM,
  ChatLLMRequest,
  generateConversationTitle,
  UserQuestion,
} from "@/api/enrichment";
import {
  useConversations,
  useConversation,
  useCreateConversation,
  useUpdateConversation,
} from "@/api/conversation";
import { Content } from "@/models/document";
import { FiltersContext } from "@/models/filter";
import { BackendWidget } from "@/models/widget-v2";
import {
  Message as ConversationMessage,
  AssistantMode,
  getMessageToolCalls,
  getMessagePendingQuestion,
} from "@/models/conversation";
import { Z_INDEX } from "@/utils/z-index";
import logger from "@/utils/logger";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  FaPlus,
  FaComments,
  FaCheck,
  FaChevronDown,
  FaExpand,
  FaCompress,
} from "react-icons/fa";
import { HiOutlineSparkles, HiX, HiOutlineExternalLink } from "react-icons/hi";
import { useLocation } from "react-router";
import { useSplitView } from "@/components/layout/split-view/SplitViewProvider";
import useTranslation from "@/hooks/use-translation";

// Shared chat components
import ChatInput from "@/components/chat/chat-input";
import ChatMessages from "@/components/chat/chat-messages";
import QuickActions from "@/components/chat/quick-actions";
import { DARK_THEME } from "@/components/chat/chat-constants";
import { resolveAnswerLabel } from "@/components/chat/question-prompt";
import {
  ChatMessage,
  ToolCallEntry,
  QuickAction,
  ChatTheme,
  UserQuestion as UIUserQuestion,
  isWidgetCreationCall,
  isWidgetRetrievalCall,
  isWidgetResult,
  parseWidgetFromResult,
  extractWidgetData,
} from "@/components/chat/types";

// Chat display mode: popup (floating window) or panel (full height sidebar)
type ChatDisplayMode = "popup" | "panel";

/**
 * Extract a pending user question from a ChatMessage.
 * Checks the explicit `userQuestion` field first, then falls back to
 * looking for an `ask_user` tool call whose args contain the questions.
 */
function extractPendingQuestion(msg: ChatMessage): UIUserQuestion | null {
  if (msg.userQuestion) return msg.userQuestion;

  const askUserCall = msg.toolCalls?.find(
    (tc) => tc.name === "ask_user" && (tc.args?.questions || tc.args?.question),
  );
  if (!askUserCall?.args) return null;

  const args = askUserCall.args as Record<string, unknown>;

  if (args.questions) {
    const rawQuestions = args.questions as Array<Record<string, unknown>>;
    return {
      questions: rawQuestions.map((q) => ({
        question: (q.question as string) || "",
        options: (q.options as Array<{ id: string; label: string }>) || [],
        allowMultiple: (q.allow_multiple ??
          q.allowMultiple ??
          false) as boolean,
      })),
      title: args.title as string | undefined,
    };
  }

  return {
    questions: [
      {
        question: args.question as string,
        options: (args.options as Array<{ id: string; label: string }>) || [],
        allowMultiple: (args.allow_multiple ??
          args.allowMultiple ??
          false) as boolean,
      },
    ],
    title: args.title as string | undefined,
  };
}

// ============================================================================
// Types
// ============================================================================

interface LLMTabConfig {
  name: string;
  description?: string | null;
  widgets: BackendWidget[];
  icon?: string | null;
  color?: string | null;
}

interface DataChatProps {
  filtersCtx?: FiltersContext;
  onAddWidgetToDashboard?: (widget: BackendWidget) => void;
  onAddTabToDashboard?: (tabConfig: LLMTabConfig) => void;
}

// ============================================================================
// Extended Theme (with extra properties for DataChat)
// ============================================================================

interface DataChatTheme extends ChatTheme {
  bgColor: string;
  borderColor: string;
}

const DATA_CHAT_THEME: DataChatTheme = {
  ...DARK_THEME,
  bgColor: "var(--arc-sidebar-background)",
  borderColor: "var(--arc-sidebar-border-accent)",
};

// ============================================================================
// Initial Messages
// ============================================================================

const INITIAL_MESSAGES: ChatMessage[] = [];

// ============================================================================
// Main Component
// ============================================================================

/**
 * DataChat bubble component - floating chat interface with popup and panel modes.
 * Uses shared components from @/components/chat.
 *
 * Modes:
 * - Popup: Small floating window in bottom-right corner
 * - Panel: Full-height sidebar on the right side of the screen
 *
 * To open chat in a split view panel, use the button in the header
 * which leverages the global split view system.
 */

const DataChat: React.FC<DataChatProps> = ({
  filtersCtx,
  onAddWidgetToDashboard,
  onAddTabToDashboard,
}) => {
  const location = useLocation();
  const { openWithCurrentRoute } = useSplitView();
  const { t } = useTranslation();

  // Don't render DataChat on the chat page to avoid duplicate chat interfaces
  const isChatPage = location.pathname.startsWith("/chat");

  // DataChat always uses dark theme
  const theme = DATA_CHAT_THEME;

  // UI State - must be declared before early return to satisfy React hooks rules
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [displayMode, setDisplayMode] = useState<ChatDisplayMode>("popup");

  // Panel width in pixels (for resizing)
  const [panelWidth, setPanelWidth] = useState(420);
  const isResizingRef = useRef(false);
  const minPanelWidth = 320;
  const maxPanelWidth = 600;

  // Conversation management hooks
  const { data: conversations = [], refetch: refetchConversations } =
    useConversations();
  const createConversationMutation = useCreateConversation();
  const updateConversationMutation = useUpdateConversation();

  // Conversation State
  const [currentConversationId, setCurrentConversationId] = useState<
    string | undefined
  >(undefined);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load current conversation data
  const { data: currentConversation } = useConversation(currentConversationId);

  // Chat State
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [toolCallsTimeline, setToolCallsTimeline] = useState<ToolCallEntry[]>(
    [],
  );
  const [documentsFromSearch, setDocumentsFromSearch] = useState<Content[]>([]);
  const [isFinishing, setIsFinishing] = useState(false);
  // Pending user question (from ask_user tool)
  const [pendingQuestion, setPendingQuestion] = useState<UIUserQuestion | null>(
    null,
  );

  // Assistant mode state
  const [mode, setMode] = useState<AssistantMode>(() => {
    return currentConversation?.mode || "agent";
  });

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const documentsFromSearchRef = useRef<Content[]>([]);
  const toolCallsTimelineRef = useRef<ToolCallEntry[]>([]);
  const isFirstMessageRef = useRef(true);
  const pendingQuestionRef = useRef<UIUserQuestion | null>(null);

  // Default filters
  const defaultFiltersCtx: FiltersContext = {
    filtersData: {
      search: "",
      filters: [],
      sentimentRangesDefinition: [],
      sort: { key: "date", asc: false },
    },
    saveTime: new Date(),
    availableFilters: undefined,
  };

  const currentFiltersCtx = filtersCtx || defaultFiltersCtx;

  // ============================================================================
  // Effects
  // ============================================================================

  useEffect(() => {
    if (typeof window !== "undefined") {
      requestAnimationFrame(() => setMounted(true));

      const styleId = "data-chat-animations";
      let style = document.getElementById(styleId) as HTMLStyleElement | null;
      if (!style) {
        style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          @keyframes fadeSlideIn {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `;
        document.head.appendChild(style);
      }

      return () => {
        style?.remove();
      };
    }
  }, []);

  // Scroll automatique lors des nouveaux messages
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen, isLoading, streamingText]);

  useEffect(() => {
    documentsFromSearchRef.current = documentsFromSearch;
  }, [documentsFromSearch]);

  // Keep toolCallsTimelineRef in sync with state for onDone callback
  useEffect(() => {
    toolCallsTimelineRef.current = toolCallsTimeline;
  }, [toolCallsTimeline]);

  // Load messages and mode when conversation changes
  useEffect(() => {
    if (
      currentConversation?.messages &&
      currentConversation.messages.length > 0
    ) {
      const loadedMessages: ChatMessage[] = currentConversation.messages.map(
        (m) => {
          const toolCalls = getMessageToolCalls(m);
          const pendingQ = getMessagePendingQuestion(m);
          return {
            id: m.id,
            text: m.text,
            sender: m.sender,
            timestamp: new Date(m.timestamp),
            toolCalls: toolCalls?.map((tc) => ({
              ...tc,
              timestamp: new Date(tc.timestamp),
            })),
            ...(pendingQ && { userQuestion: pendingQ }),
          };
        },
      );
      setMessages(loadedMessages);
      isFirstMessageRef.current = false;

      // Restore pendingQuestion from the last assistant message that has one
      let restoredQuestion = false;
      for (let i = loadedMessages.length - 1; i >= 0; i--) {
        const msg = loadedMessages[i];
        if (msg.sender !== "assistant") continue;
        const question = extractPendingQuestion(msg);
        if (question) {
          const hasAnswer = loadedMessages
            .slice(i + 1)
            .some((m) => m.userQuestionResponse);
          if (!hasAnswer) {
            setPendingQuestion(question);
            restoredQuestion = true;
          }
          break;
        }
      }
      if (!restoredQuestion) {
        setPendingQuestion(null);
      }

      if (currentConversation.mode) {
        setMode(currentConversation.mode);
      }
    } else if (!currentConversationId) {
      setMessages(INITIAL_MESSAGES);
      isFirstMessageRef.current = true;
      setMode("agent");
    }
  }, [currentConversation, currentConversationId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isDropdownOpen]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleOpen = () => {
    setIsClosing(false);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      // Reset to popup mode when closing
      setDisplayMode("popup");
    }, 200);
  };

  const handleExpandToPanel = () => {
    setDisplayMode("panel");
  };

  const handleCollapseToPopup = () => {
    setDisplayMode("popup");
  };

  // Handle panel resize
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizingRef.current) return;
      const newWidth = window.innerWidth - moveEvent.clientX;
      setPanelWidth(Math.min(maxPanelWidth, Math.max(minPanelWidth, newWidth)));
    };

    const handleMouseUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  const handleOpenInSplitView = useCallback(() => {
    // Close the popup
    handleClose();

    // Open chat in split view
    // Build route with conversation ID if one exists
    const route = currentConversationId
      ? `/chat?id=${currentConversationId}`
      : "/chat";

    openWithCurrentRoute({
      type: "chat",
      id: currentConversationId || "chat",
      route,
      title: "Chat",
    });
  }, [openWithCurrentRoute, currentConversationId]);

  const handleQuickAction = (action: QuickAction) => {
    setInputValue(action.prompt);
  };

  // Conversation handlers
  const handleSelectConversation = useCallback((conversationId: string) => {
    setCurrentConversationId(conversationId);
    setIsDropdownOpen(false);
  }, []);

  const handleNewConversation = useCallback(() => {
    setCurrentConversationId(undefined);
    setMessages(INITIAL_MESSAGES);
    isFirstMessageRef.current = true;
    setIsDropdownOpen(false);
    setMode("agent");
  }, []);

  // Handle mode change
  const handleModeChange = useCallback(
    (newMode: AssistantMode) => {
      setMode(newMode);
      if (currentConversationId) {
        updateConversationMutation.mutate({
          uriParams: { id: currentConversationId },
          mode: newMode,
        });
      }
    },
    [currentConversationId, updateConversationMutation],
  );

  // Convert local messages to API format
  const convertMessagesToApi = useCallback(
    (msgs: ChatMessage[]): ConversationMessage[] => {
      return msgs
        .filter((m) => m.id !== "welcome")
        .map((m) => ({
          id: m.id,
          text: m.text,
          sender: m.sender,
          timestamp: m.timestamp.toISOString(),
          tool_calls: m.toolCalls?.map((tc) => ({
            id: tc.id,
            name: tc.name,
            args: tc.args,
            status: tc.status,
            result: tc.result,
            timestamp: tc.timestamp.toISOString(),
          })),
          ...(m.userQuestion && {
            pending_question: {
              questions: m.userQuestion.questions.map((q) => ({
                question: q.question,
                options: q.options,
                allow_multiple: q.allowMultiple ?? false,
              })),
              title: m.userQuestion.title,
            },
          }),
        }));
    },
    [],
  );

  // Save messages to conversation
  const saveMessagesToConversation = useCallback(
    async (conversationId: string, msgs: ChatMessage[]) => {
      try {
        await updateConversationMutation.mutateAsync({
          uriParams: { id: conversationId },
          messages: convertMessagesToApi(msgs),
        });
      } catch (error) {
        logger.error("Failed to save messages:", error);
      }
    },
    [updateConversationMutation, convertMessagesToApi],
  );

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputValue,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const userInput = inputValue;
    setInputValue("");
    setIsLoading(true);
    setStreamingText("");
    setToolCallsTimeline([]);
    setDocumentsFromSearch([]);

    // Create conversation on first message if none exists
    let conversationId = currentConversationId;
    if (!conversationId && isFirstMessageRef.current) {
      try {
        const newConversation = await createConversationMutation.mutateAsync({
          name: "Nouvelle conversation",
          messages: convertMessagesToApi([userMessage]),
          mode: mode,
        });
        conversationId = newConversation.id;
        setCurrentConversationId(conversationId);
        isFirstMessageRef.current = false;
        refetchConversations();

        // Generate title asynchronously
        generateConversationTitle(userInput)
          .then((generatedTitle) => {
            updateConversationMutation.mutate({
              uriParams: { id: newConversation.id! },
              name: generatedTitle,
            });
            refetchConversations();
          })
          .catch((err) => logger.error("Failed to generate title:", err));
      } catch (error) {
        logger.error("Failed to create conversation:", error);
      }
    }

    // Build history with tool calls for context
    const history = messages.slice(INITIAL_MESSAGES.length).map((msg) => {
      if (msg.toolCalls && msg.toolCalls.length > 0) {
        const toolCallsText = msg.toolCalls
          .map((tc) => {
            const argsStr = tc.args ? JSON.stringify(tc.args) : "";
            const resultStr = tc.result ? JSON.stringify(tc.result) : "";
            return `[Tool: ${tc.name}${argsStr ? `, args: ${argsStr}` : ""}${resultStr ? `, result: ${resultStr}` : ""}]`;
          })
          .join("\n");
        return msg.text ? `${toolCallsText}\n${msg.text}` : toolCallsText;
      }
      return msg.text;
    });

    const request: ChatLLMRequest = {
      history: history,
      message: userInput,
      mode: mode,
    };

    let fullText = "";

    abortControllerRef.current = await streamChatLLM(
      request,
      (chunk: string) => {
        fullText += chunk;
        setStreamingText(fullText);
      },
      () => {
        setIsFinishing(true);
        const finalTimeline = [...toolCallsTimelineRef.current];
        const questionForMessage = pendingQuestionRef.current;

        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: fullText,
          sender: "assistant",
          timestamp: new Date(),
          toolCalls: finalTimeline.length > 0 ? finalTimeline : undefined,
          ...(questionForMessage && { userQuestion: questionForMessage }),
        };

        setMessages((prev) => {
          const newMessages = [...prev, assistantMessage];
          if (conversationId) {
            saveMessagesToConversation(conversationId, newMessages);
          }
          return newMessages;
        });
        setToolCallsTimeline([]);
        pendingQuestionRef.current = null;
        setIsLoading(false);
        setStreamingText("");
        setIsFinishing(false);
      },
      (error: Error) => {
        setIsFinishing(true);
        const finalTimeline = [...toolCallsTimelineRef.current];

        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: `Une erreur s'est produite. Veuillez réessayer.`,
          sender: "assistant",
          timestamp: new Date(),
          toolCalls: finalTimeline.length > 0 ? finalTimeline : undefined,
        };

        setMessages((prev) => [...prev, errorMessage]);
        setToolCallsTimeline([]);
        setIsLoading(false);
        setStreamingText("");
        setIsFinishing(false);
        logger.error("Chat error:", error);
      },
      (toolCall) => {
        let parsedArgs = toolCall.args;
        if (typeof parsedArgs === "string") {
          try {
            parsedArgs = JSON.parse(parsedArgs);
          } catch {
            // Keep as is
          }
        }

        const newEntry: ToolCallEntry = {
          id: `tc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: toolCall.name,
          args: parsedArgs as Record<string, unknown>,
          status: "pending",
          timestamp: new Date(),
        };
        setToolCallsTimeline((prev) => [...prev, newEntry]);

        // Handle display_documents
        if (toolCall.name === "display_documents") {
          try {
            const docIds = (parsedArgs?.document_ids || []) as string[];
            const comment = (parsedArgs as Record<string, unknown>)?.comment as
              | string
              | undefined;

            const docsToDisplay = docIds
              .map((id) =>
                documentsFromSearchRef.current.find(
                  (doc) => doc.id === id || doc.normalized?.id === id,
                ),
              )
              .filter(Boolean) as Content[];

            if (docsToDisplay.length > 0) {
              const docMessage: ChatMessage = {
                id: `docs-${Date.now()}`,
                text: comment || "",
                sender: "assistant",
                timestamp: new Date(),
                documents: docsToDisplay,
              };
              setMessages((prev) => [...prev, docMessage]);
            }
          } catch (error) {
            logger.error("Error parsing display_documents:", error);
          }
        }
      },
      (result) => {
        // Find the matching pending tool call to get its args
        const matchingToolCall = toolCallsTimelineRef.current.find(
          (tc) => tc.name === result.name && tc.status === "pending",
        );

        setToolCallsTimeline((prev) => {
          const updated = [...prev];
          for (let i = updated.length - 1; i >= 0; i--) {
            if (
              updated[i].name === result.name &&
              updated[i].status === "pending"
            ) {
              updated[i] = {
                ...updated[i],
                status: "completed",
                result: result.result,
              };
              break;
            }
          }
          return updated;
        });

        if (
          result.name === "search_elasticsearch" &&
          Array.isArray(result.result)
        ) {
          const rawDocs = result.result as Record<string, unknown>[];
          const docs = rawDocs.map(
            (doc, idx) =>
              ({
                id: (doc.id as string) || `doc-${Date.now()}-${idx}`,
                normalized: {
                  id: doc.id as string,
                  text: (doc.text as string) || "",
                  date: doc.date as string,
                  url: doc.url as string,
                  language: doc.language as string,
                  media: (doc.media as unknown[]) || [],
                },
                extra: {
                  lang: (doc.language as string) || "fr",
                },
              }) as Content,
          );

          documentsFromSearchRef.current = docs;
          setDocumentsFromSearch(docs);
        }

        // Check for widget creation or retrieval from call_api (live stream only)
        if (result.name === "call_api" && matchingToolCall) {
          const isWidgetCall =
            isWidgetCreationCall(result.name, matchingToolCall.args) ||
            isWidgetRetrievalCall(result.name, matchingToolCall.args);

          if (isWidgetCall) {
            const widgetData = extractWidgetData(result.result);

            if (isWidgetResult(widgetData)) {
              const widget = parseWidgetFromResult(widgetData);

              const widgetMessage: ChatMessage = {
                id: `widget-${Date.now()}`,
                text: "",
                sender: "assistant",
                timestamp: new Date(),
                widget: widget,
              };
              setMessages((prev) => [...prev, widgetMessage]);
            }
          }
        }

        // Legacy: handle generate_widget tool (if it exists)
        if (
          result.name === "generate_widget" &&
          result.result &&
          typeof result.result === "object" &&
          !Array.isArray(result.result)
        ) {
          const widgetConfig = result.result as Record<string, unknown>;
          const widgetType = widgetConfig.type as string;
          const widget: BackendWidget = {
            id: (widgetConfig.id as string) || `chat-widget-${Date.now()}`,
            // IMPORTANT: doc_type is required for backend to dispatch to correct widget subclass
            doc_type: widgetType,
            chart: widgetType as BackendWidget["chart"],
            metadata: {
              name: (widgetConfig.name as string) || "Widget généré",
            },
            ...widgetConfig,
          };
          const widgetMessage: ChatMessage = {
            id: `widget-${Date.now()}`,
            text: "",
            sender: "assistant",
            timestamp: new Date(),
            widget: widget,
          };
          setMessages((prev) => [...prev, widgetMessage]);
        }

        if (
          result.name === "generate_tab" &&
          result.result &&
          typeof result.result === "object" &&
          !Array.isArray(result.result)
        ) {
          const tabResult = result.result as Record<string, unknown>;
          const rawWidgets = (tabResult.widgets || []) as Record<
            string,
            unknown
          >[];

          const widgets: BackendWidget[] = rawWidgets.map((w, idx) => ({
            id: (w.id as string) || `tab-widget-${Date.now()}-${idx}`,
            // IMPORTANT: doc_type is required for backend to dispatch to correct widget subclass
            doc_type: w.type as string,
            chart: w.type as BackendWidget["chart"],
            metadata: { name: (w.name as string) || `Widget ${idx + 1}` },
            ...w,
          }));

          const tabConfig: LLMTabConfig = {
            name: (tabResult.name as string) || "Nouvel onglet",
            description: tabResult.description as string | null,
            widgets,
            icon: tabResult.icon as string | null,
            color: tabResult.color as string | null,
          };

          if (onAddTabToDashboard) {
            onAddTabToDashboard(tabConfig);

            const confirmMessage: ChatMessage = {
              id: `tab-confirm-${Date.now()}`,
              text: t("tab_created_with_n_widgets", {
                count: widgets.length,
                name: tabConfig.name,
              }),
              sender: "assistant",
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, confirmMessage]);
          }
        }
      },
      // onUserQuestion
      (question: UserQuestion) => {
        const updatedTimeline = toolCallsTimelineRef.current.map((tc) =>
          tc.name === "ask_user" && tc.status === "pending"
            ? {
                ...tc,
                status: "completed" as const,
                result: { questions: question.questions },
              }
            : tc,
        );
        toolCallsTimelineRef.current = updatedTimeline;
        setToolCallsTimeline([...updatedTimeline]);

        const uiQuestion: UIUserQuestion = {
          questions: question.questions.map((q) => ({
            question: q.question,
            options: q.options,
            allowMultiple: q.allowMultiple,
          })),
          title: question.title,
        };

        pendingQuestionRef.current = uiQuestion;
        setPendingQuestion(uiQuestion);
      },
    );
  }, [
    inputValue,
    isLoading,
    messages,
    onAddTabToDashboard,
    currentConversationId,
    createConversationMutation,
    convertMessagesToApi,
    refetchConversations,
    updateConversationMutation,
    saveMessagesToConversation,
    mode,
  ]);

  // Handle stopping the current generation
  const handleStop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const currentText = streamingText || "";
    if (currentText.trim()) {
      const finalToolCalls = [...toolCallsTimelineRef.current];
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: currentText,
        sender: "assistant",
        timestamp: new Date(),
        toolCalls: finalToolCalls.length > 0 ? finalToolCalls : undefined,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    }

    setStreamingText("");
    setToolCallsTimeline([]);
    setDocumentsFromSearch([]);
    toolCallsTimelineRef.current = [];
    pendingQuestionRef.current = null;
    setIsLoading(false);
    setIsFinishing(false);
  }, [streamingText]);

  // Handle editing a previous user message (truncate history and prefill input)
  const handleEditMessage = useCallback(
    (messageId: string, text: string) => {
      if (isLoading) return;

      const idx = messages.findIndex((m) => m.id === messageId);
      if (idx === -1) return;

      setMessages(messages.slice(0, idx));
      setInputValue(text);
      setPendingQuestion(null);
    },
    [messages, isLoading],
  );

  // Handle user answering questions (supports multiple questions at once)
  const handleQuestionAnswer = useCallback(
    (answers: Record<number, string[]>) => {
      if (!pendingQuestion) return;

      const answerParts: string[] = [];
      const allSelectedIds: string[] = [];

      pendingQuestion.questions.forEach((q, idx) => {
        const selectedIds = answers[idx] || [];
        allSelectedIds.push(...selectedIds);
        const labels = selectedIds.map((id) =>
          resolveAnswerLabel(id, q.options),
        );
        if (pendingQuestion.questions.length === 1) {
          answerParts.push(`J'ai choisi: ${labels.join(", ")}`);
        } else {
          answerParts.push(`${q.question} → ${labels.join(", ")}`);
        }
      });

      const answerText =
        pendingQuestion.questions.length === 1
          ? answerParts[0]
          : `Mes réponses:\n${answerParts.map((p) => `- ${p}`).join("\n")}`;

      const questionMessage: ChatMessage = {
        id: Date.now().toString(),
        text: answerText,
        sender: "user",
        timestamp: new Date(),
        userQuestion: pendingQuestion,
        userQuestionResponse: allSelectedIds,
      };

      setMessages((prev) => {
        const updated = prev.map((m) =>
          m.userQuestion && m.sender === "assistant"
            ? { ...m, userQuestion: undefined }
            : m,
        );
        return [...updated, questionMessage];
      });
      setPendingQuestion(null);

      // Automatically continue the conversation with the answer
      setIsLoading(true);
      setStreamingText("");
      setToolCallsTimeline([]);
      setDocumentsFromSearch([]);

      // Build history including the question response
      const history = [...messages, questionMessage]
        .slice(INITIAL_MESSAGES.length)
        .map((msg) => {
          if (msg.userQuestion && msg.userQuestionResponse) {
            const allOptions = msg.userQuestion.questions.flatMap(
              (q) => q.options,
            );
            const labels = msg.userQuestionResponse.map((id) =>
              resolveAnswerLabel(id, allOptions),
            );
            const qTexts = msg.userQuestion.questions
              .map((q) => q.question)
              .join(" / ");
            return `[Questions: ${qTexts}] User answered: ${labels.join(", ")}`;
          }
          if (msg.toolCalls && msg.toolCalls.length > 0) {
            const toolCallsText = msg.toolCalls
              .map((tc) => {
                const argsStr = tc.args ? JSON.stringify(tc.args) : "";
                const resultStr = tc.result ? JSON.stringify(tc.result) : "";
                return `[Tool: ${tc.name}${argsStr ? `, args: ${argsStr}` : ""}${resultStr ? `, result: ${resultStr}` : ""}]`;
              })
              .join("\n");
            return msg.text ? `${toolCallsText}\n${msg.text}` : toolCallsText;
          }
          return msg.text;
        });

      const request: ChatLLMRequest = {
        history: history,
        message: answerText,
        mode: mode,
      };

      let fullText = "";

      streamChatLLM(
        request,
        (chunk: string) => {
          fullText += chunk;
          setStreamingText(fullText);
        },
        () => {
          setIsFinishing(true);
          const finalTimeline = [...toolCallsTimelineRef.current];
          const questionForMessage = pendingQuestionRef.current;

          const assistantMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            text: fullText,
            sender: "assistant",
            timestamp: new Date(),
            toolCalls: finalTimeline.length > 0 ? finalTimeline : undefined,
            ...(questionForMessage && { userQuestion: questionForMessage }),
          };

          setMessages((prev) => {
            const newMessages = [...prev, assistantMessage];
            if (currentConversationId) {
              saveMessagesToConversation(currentConversationId, newMessages);
            }
            return newMessages;
          });
          setToolCallsTimeline([]);
          pendingQuestionRef.current = null;
          setIsLoading(false);
          setStreamingText("");
          setIsFinishing(false);
        },
        (error: Error) => {
          setIsFinishing(true);
          const errorMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            text: `Une erreur s'est produite. Veuillez réessayer.`,
            sender: "assistant",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
          setToolCallsTimeline([]);
          setIsLoading(false);
          setStreamingText("");
          setIsFinishing(false);
          logger.error("Chat error:", error);
        },
        (toolCall) => {
          let parsedArgs = toolCall.args;
          if (typeof parsedArgs === "string") {
            try {
              parsedArgs = JSON.parse(parsedArgs);
            } catch {
              // Keep as is
            }
          }
          const newEntry: ToolCallEntry = {
            id: `tc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: toolCall.name,
            args: parsedArgs as Record<string, unknown>,
            status: "pending",
            timestamp: new Date(),
          };
          setToolCallsTimeline((prev) => [...prev, newEntry]);
        },
        (result) => {
          setToolCallsTimeline((prev) => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (
                updated[i].name === result.name &&
                updated[i].status === "pending"
              ) {
                updated[i] = {
                  ...updated[i],
                  status: "completed",
                  result: result.result,
                };
                break;
              }
            }
            return updated;
          });
        },
        (question: UserQuestion) => {
          const uiQuestion: UIUserQuestion = {
            questions: question.questions.map((q) => ({
              question: q.question,
              options: q.options,
              allowMultiple: q.allowMultiple,
            })),
            title: question.title,
          };
          pendingQuestionRef.current = uiQuestion;
          setPendingQuestion(uiQuestion);
        },
      );
    },
    [
      pendingQuestion,
      messages,
      mode,
      currentConversationId,
      saveMessagesToConversation,
    ],
  );

  // ============================================================================
  // Render Chat Panel
  // ============================================================================

  const renderChatPanel = () => (
    <div
      className={`
        flex h-full w-full flex-col overflow-hidden
        rounded-2xl
        ${theme.bg} ${theme.shadow}
        ${theme.border}
        transition-all duration-200 ease-out
        ${
          isClosing
            ? "opacity-0 translate-y-4 scale-95"
            : "opacity-100 translate-y-0 scale-100"
        }
      `}
    >
      {/* Header */}
      <div
        className={`
          flex items-center justify-between px-4 py-3
          ${theme.border} border-b
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[rgba(99,102,241,0.1)] flex-shrink-0">
            <HiOutlineSparkles className="h-4 w-4 text-accent" />
          </div>

          {/* Conversation Dropdown */}
          <div className="relative flex-1 min-w-0" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`
                flex items-center gap-2 px-2 py-1 rounded-lg w-full min-w-0
                ${theme.buttonHover} transition-colors duration-150
                hover:bg-[rgba(99,102,241,0.1)]
              `}
            >
              <span
                className={`text-sm font-medium ${theme.textPrimary} truncate`}
              >
                {currentConversation?.name || "Nouvelle conversation"}
              </span>
              <FaChevronDown
                className={`h-3 w-3 ${theme.textMuted} flex-shrink-0 transition-transform duration-150 ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div
                className={`
                  absolute top-full left-0 mt-1 w-64 max-h-80 overflow-y-auto
                  rounded-xl ${theme.bg} ${theme.border} border
                  shadow-lg z-50
                  animate-[fadeSlideIn_0.15s_ease-out]
                `}
              >
                {/* New Conversation Option */}
                <button
                  onClick={handleNewConversation}
                  className={`
                    flex items-center gap-2 w-full px-3 py-2.5
                    ${theme.textPrimary} ${theme.buttonHover}
                    transition-colors duration-150
                    border-b ${theme.border}
                  `}
                >
                  <FaPlus className="h-3 w-3 text-accent" />
                  <span className="text-sm font-medium">
                    Nouvelle conversation
                  </span>
                </button>

                {/* Conversations List */}
                {conversations.length > 0 ? (
                  <div className="py-1">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => handleSelectConversation(conv.id!)}
                        className={`
                          flex items-center gap-2 w-full px-3 py-2
                          ${theme.textSecondary} ${theme.buttonHover}
                          transition-colors duration-150
                          ${currentConversationId === conv.id ? "bg-[rgba(99,102,241,0.15)] text-accent" : ""}
                        `}
                      >
                        <FaComments className="h-3 w-3 flex-shrink-0" />
                        <span className="text-sm truncate">
                          {conv.name || "Sans titre"}
                        </span>
                        {currentConversationId === conv.id && (
                          <FaCheck className="h-2.5 w-2.5 ml-auto text-accent flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={`px-3 py-4 text-center ${theme.textMuted}`}>
                    <p className="text-xs">Aucune conversation</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Expand/Collapse button */}
          <button
            onClick={
              displayMode === "popup"
                ? handleExpandToPanel
                : handleCollapseToPopup
            }
            className={`
              flex h-8 w-8 items-center justify-center rounded-lg
              ${theme.textSecondary} ${theme.buttonHover}
              transition-colors duration-150
            `}
            aria-label={displayMode === "popup" ? "Agrandir" : "Réduire"}
            title={displayMode === "popup" ? "Agrandir" : "Réduire"}
          >
            {displayMode === "popup" ? (
              <FaExpand className="h-3.5 w-3.5" />
            ) : (
              <FaCompress className="h-3.5 w-3.5" />
            )}
          </button>
          {/* Open in Split View button */}
          <button
            onClick={handleOpenInSplitView}
            className={`
              flex h-8 w-8 items-center justify-center rounded-lg
              ${theme.textSecondary} ${theme.buttonHover}
              transition-colors duration-150
            `}
            aria-label="Ouvrir dans un panel"
            title="Ouvrir dans un panel"
          >
            <HiOutlineExternalLink className="h-4 w-4" />
          </button>
          {/* Close button */}
          <button
            onClick={handleClose}
            className={`
              flex h-8 w-8 items-center justify-center rounded-lg
              ${theme.textSecondary} ${theme.buttonHover}
              transition-colors duration-150
            `}
            aria-label="Fermer"
          >
            <HiX className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <ChatMessages
        messages={messages}
        theme={theme}
        filtersCtx={currentFiltersCtx}
        onAddWidgetToDashboard={onAddWidgetToDashboard}
        isLoading={isLoading}
        isFinishing={isFinishing}
        streamingText={streamingText}
        toolCallsTimeline={toolCallsTimeline}
        documentsFromSearch={documentsFromSearch}
        pendingQuestion={pendingQuestion}
        onQuestionAnswer={handleQuestionAnswer}
        onEditMessage={handleEditMessage}
      />

      {/* Quick Actions */}
      <div className={`px-4 pb-2 ${theme.border} border-t pt-3`}>
        <QuickActions
          onAction={handleQuickAction}
          disabled={isLoading}
          theme={theme}
        />
      </div>

      {/* Input Area */}
      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSendMessage}
        onStop={handleStop}
        isLoading={isLoading}
        mode={mode}
        onModeChange={handleModeChange}
        disabled={isLoading}
        theme={theme}
      />

      <div ref={messagesEndRef} />
    </div>
  );

  // ============================================================================
  // Early Returns
  // ============================================================================

  // Don't render on chat page
  if (isChatPage) {
    return null;
  }

  if (typeof window === "undefined") return null;
  if (!mounted || !document.body) return null;

  // ============================================================================
  // Popup Mode (only mode now)
  // ============================================================================

  return createPortal(
    <div>
      {/* Floating Action Button - hidden when panel mode is active */}
      <button
        onClick={isOpen ? handleClose : handleOpen}
        className={`
          group fixed bottom-6 right-6 flex h-14 w-14 items-center justify-center
          rounded-full transition-all duration-300
          ${isOpen && displayMode === "panel" ? "opacity-0 pointer-events-none scale-75" : ""}
          ${isOpen && displayMode === "popup" ? "rotate-0 scale-90" : "rotate-0 scale-100"}
          bg-accent text-white shadow-arc-accent
          hover:bg-accent-light hover:shadow-arc-accent-lg hover:-translate-y-0.5
          active:translate-y-0 active:shadow-arc-accent active:scale-[0.98]
        `}
        style={{ zIndex: Z_INDEX.DATA_CHAT + 1 }}
        aria-label={isOpen ? "Fermer le chat" : "Ouvrir le chat"}
      >
        {isOpen ? (
          <HiX className="h-6 w-6 transition-transform duration-200" />
        ) : (
          <HiOutlineSparkles className="h-6 w-6 transition-transform duration-200 group-hover:scale-110" />
        )}
      </button>

      {/* Chat Popup Window */}
      {isOpen && displayMode === "popup" && (
        <div
          style={{
            position: "fixed",
            bottom: "96px",
            right: "24px",
            width: "420px",
            height: "600px",
            zIndex: Z_INDEX.DATA_CHAT,
          }}
        >
          {renderChatPanel()}
        </div>
      )}

      {/* Chat Panel Mode - Full height sidebar on the right */}
      {isOpen && displayMode === "panel" && (
        <div
          className="fixed inset-y-0 right-0 flex"
          style={{ zIndex: Z_INDEX.DATA_CHAT }}
        >
          {/* Resize handle */}
          <div
            className="w-1 cursor-col-resize hover:bg-accent/50 active:bg-accent/70 transition-colors"
            onMouseDown={handleResizeStart}
            style={{
              backgroundColor: isResizingRef.current
                ? "var(--accent)"
                : "transparent",
            }}
          />
          {/* Panel content */}
          <div
            className={`
              h-full flex flex-col overflow-hidden
              ${theme.bg} ${theme.shadow}
              border-l ${theme.border}
              transition-all duration-200 ease-out
              ${isClosing ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}
            `}
            style={{ width: `${panelWidth}px` }}
          >
            {/* Header */}
            <div
              className={`
                flex items-center justify-between px-4 py-3
                ${theme.border} border-b
              `}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[rgba(99,102,241,0.1)] flex-shrink-0">
                  <HiOutlineSparkles className="h-4 w-4 text-accent" />
                </div>

                {/* Conversation Dropdown */}
                <div className="relative flex-1 min-w-0" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`
                      flex items-center gap-2 px-2 py-1 rounded-lg w-full min-w-0
                      ${theme.buttonHover} transition-colors duration-150
                      hover:bg-[rgba(99,102,241,0.1)]
                    `}
                  >
                    <span
                      className={`text-sm font-medium ${theme.textPrimary} truncate`}
                    >
                      {currentConversation?.name || "Nouvelle conversation"}
                    </span>
                    <FaChevronDown
                      className={`h-3 w-3 ${theme.textMuted} flex-shrink-0 transition-transform duration-150 ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div
                      className={`
                        absolute top-full left-0 mt-1 w-64 max-h-80 overflow-y-auto
                        rounded-xl ${theme.bg} ${theme.border} border
                        shadow-lg z-50
                        animate-[fadeSlideIn_0.15s_ease-out]
                      `}
                    >
                      {/* New Conversation Option */}
                      <button
                        onClick={handleNewConversation}
                        className={`
                          flex items-center gap-2 w-full px-3 py-2.5
                          ${theme.textPrimary} ${theme.buttonHover}
                          transition-colors duration-150
                          border-b ${theme.border}
                        `}
                      >
                        <FaPlus className="h-3 w-3 text-accent" />
                        <span className="text-sm font-medium">
                          Nouvelle conversation
                        </span>
                      </button>

                      {/* Conversations List */}
                      {conversations.length > 0 ? (
                        <div className="py-1">
                          {conversations.map((conv) => (
                            <button
                              key={conv.id}
                              onClick={() => handleSelectConversation(conv.id!)}
                              className={`
                                flex items-center gap-2 w-full px-3 py-2
                                ${theme.textSecondary} ${theme.buttonHover}
                                transition-colors duration-150
                                ${currentConversationId === conv.id ? "bg-[rgba(99,102,241,0.15)] text-accent" : ""}
                              `}
                            >
                              <FaComments className="h-3 w-3 flex-shrink-0" />
                              <span className="text-sm truncate">
                                {conv.name || "Sans titre"}
                              </span>
                              {currentConversationId === conv.id && (
                                <FaCheck className="h-2.5 w-2.5 ml-auto text-accent flex-shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div
                          className={`px-3 py-4 text-center ${theme.textMuted}`}
                        >
                          <p className="text-xs">Aucune conversation</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Collapse to popup button */}
                <button
                  onClick={handleCollapseToPopup}
                  className={`
                    flex h-8 w-8 items-center justify-center rounded-lg
                    ${theme.textSecondary} ${theme.buttonHover}
                    transition-colors duration-150
                  `}
                  aria-label="Réduire"
                  title="Réduire"
                >
                  <FaCompress className="h-3.5 w-3.5" />
                </button>
                {/* Open in Split View button */}
                <button
                  onClick={handleOpenInSplitView}
                  className={`
                    flex h-8 w-8 items-center justify-center rounded-lg
                    ${theme.textSecondary} ${theme.buttonHover}
                    transition-colors duration-150
                  `}
                  aria-label="Ouvrir dans un panel"
                  title="Ouvrir dans un panel"
                >
                  <HiOutlineExternalLink className="h-4 w-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className={`
                    flex h-8 w-8 items-center justify-center rounded-lg
                    ${theme.textSecondary} ${theme.buttonHover}
                    transition-colors duration-150
                  `}
                  aria-label="Fermer"
                >
                  <HiX className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <ChatMessages
              messages={messages}
              theme={theme}
              filtersCtx={currentFiltersCtx}
              onAddWidgetToDashboard={onAddWidgetToDashboard}
              isLoading={isLoading}
              isFinishing={isFinishing}
              streamingText={streamingText}
              toolCallsTimeline={toolCallsTimeline}
              documentsFromSearch={documentsFromSearch}
              pendingQuestion={pendingQuestion}
              onQuestionAnswer={handleQuestionAnswer}
              onEditMessage={handleEditMessage}
            />

            {/* Quick Actions */}
            <div className={`px-4 pb-2 ${theme.border} border-t pt-3`}>
              <QuickActions
                onAction={handleQuickAction}
                disabled={isLoading}
                theme={theme}
              />
            </div>

            {/* Input Area */}
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSendMessage}
              onStop={handleStop}
              isLoading={isLoading}
              mode={mode}
              onModeChange={handleModeChange}
              disabled={isLoading}
              theme={theme}
            />

            <div ref={messagesEndRef} />
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default DataChat;
